import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimeMessage } from "@app-factory/shared";

type Listener = (message: RealtimeMessage) => void;

interface RealtimeContextValue {
  connected: boolean;
  subscribe: (fn: Listener) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * Jedno WS spojení pro celou aplikaci. Eventy invalidují dotčené TanStack
 * Query cache a zároveň jdou přes subscribe libovolným live komponentám
 * (Activity Stream). Reconnect s exponenciálním backoffem.
 */
export function RealtimeProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Set<Listener>());
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${location.host}/ws`);
      socketRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        setConnected(true);
        // po reconnectu obnovíme data, ať nic nechybí
        void queryClient.invalidateQueries();
      };
      ws.onmessage = (raw) => {
        let message: RealtimeMessage;
        try {
          message = JSON.parse(String(raw.data)) as RealtimeMessage;
        } catch {
          return;
        }
        handleInvalidation(message);
        for (const fn of listenersRef.current) fn(message);
      };
      ws.onclose = () => {
        setConnected(false);
        socketRef.current = null;
        if (!closed) {
          attempt += 1;
          const delay = Math.min(15_000, 500 * 2 ** attempt);
          reconnectTimer = setTimeout(connect, delay);
        }
      };
      ws.onerror = () => ws.close();
    };

    const handleInvalidation = (message: RealtimeMessage) => {
      switch (message.type) {
        case "audit.created":
          void queryClient.invalidateQueries({ queryKey: ["audit"] });
          break;
        case "project.created":
        case "project.updated":
        case "project.deleted":
          void queryClient.invalidateQueries({ queryKey: ["projects"] });
          break;
        case "project.section.changed":
          // cílená invalidace dat konkrétní sekce projektu (Fáze 2)
          void queryClient.invalidateQueries({
            queryKey: ["section", message.projectId, message.section],
          });
          if (message.section === "knowledge") {
            void queryClient.invalidateQueries({ queryKey: ["knowledge-doc"] });
          }
          break;
        case "membership.changed":
          void queryClient.invalidateQueries({ queryKey: ["projects"] });
          void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
          break;
        default:
          break;
      }
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socketRef.current?.close();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled, queryClient]);

  const subscribe = useCallback((fn: Listener) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  return (
    <RealtimeContext.Provider value={{ connected, subscribe }}>{children}</RealtimeContext.Provider>
  );
}

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error("useRealtime mimo RealtimeProvider");
  return ctx;
}

/** Přihlásí komponentu k odběru realtime zpráv. */
export function useRealtimeEvent(handler: Listener) {
  const { subscribe } = useRealtime();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => subscribe((m) => handlerRef.current(m)), [subscribe]);
}
